<template>
  <div class="m-fieldset row">
    <div class="col-12">
      <p class="m-fieldset__caption">
        {{ fieldset.caption ? fieldset.caption : form.name }}
      </p>
    </div>
    <template
      v-if="$isNotNullOrUndefined(fieldset) &&
        $isNotNullOrUndefined(fieldset.containers)
      "
    >
      <div
        v-for="(container, containerIndex) in fieldset.containers"
        :key="containerIndex"
        class="-container col-12"
        :class="`col-lg-${12/fieldset.containers.length}`"
      >
        <template v-if="$isNotNullOrUndefined(container.fields)">
          <div v-for="field in container.fields" :key="field.key" class="pb-3" :class="{'d-none': !isVisible(field) || isHidden(field) }">
            <template v-if="isTitleAndDescription(field) && field.extraData">
              <h2 v-if="field.extraData.caption">
                {{ field.extraData.caption }}
              </h2>
              <p v-if="field.extraData.bodyText">
                {{ field.extraData.bodyText }}
              </p>
            </template>
            <base-input
              v-if="isBaseInput(field) "
              :id="field.id"
              v-model="field.defaultValue"
              v-validate="createValidationString(field)"
              :mandatory="field.required"
              :field-id="`field_${field.id}`"
              :placeholder="field.placeholder"
              type="text"
              :label="getLabel(field)"
              :name="field.name"
              :error-message="field.invalidErrorMessage"
              :aria-label="field.label"
              @keyup.native="checkConditions()"
            />
            <base-input
              v-if="isDate(field) "
              :id="field.id"
              v-model="field.defaultValue"
              v-validate="field.regEx"
              :field-id="`field_${field.id}`"
              type="date"
              :label="field.label"
              :name="field.name"
              :error-message="field.invalidErrorMessage"
              :aria-label="field.label"
              @keyup.native="checkConditions()"
            />
            <base-input
              v-if="isHidden(field) "
              :id="field.id"
              :ref="'hiddenField_' + field.name"
              v-model="field.defaultValue"
              v-validate="field.regEx"
              :field-id="`field_${field.id}`"
              type="hidden"
              :label="field.label"
              :name="field.name"
              :error-message="field.invalidErrorMessage"
              :aria-label="field.label"
              @keyup.native="checkConditions()"
            />
            <base-input
              v-if="isPassword(field)"
              :id="field.id"
              v-model="field.defaultValue"
              v-validate="createValidationString(field)"
              :mandatory="field.required"
              :field-id="`field_${field.id}`"
              :placeholder="field.placeholder"
              type="password"
              :label="getLabel(field)"
              :name="field.name"
              :error-message="field.invalidErrorMessage"
              :aria-label="field.label"
              @keyup.native="checkConditions()"
            />
            <base-textarea
              v-if="isTextarea(field)"
              :id="field.id"
              v-model="field.defaultValue"
              v-validate="createValidationString(field)"
              :mandatory="field.required"
              :field-id="`field_${field.id}`"
              :placeholder="field.placeholder"
              type="text"
              :label="getLabel(field)"
              :name="field.name"
              :error-message="field.invalidErrorMessage"
              :aria-label="field.label"
              @keyup.native="checkConditions()"
            />
            <template v-if="isSelect(field)">
              <label class="a-input__label" :for="field.id">{{ getLabel(field) }}</label>
              <base-select
                :id="field.id"
                v-model="field.defaultValue"
                v-validate="createValidationString(field)"
                :field-id="`field_${field.id}`"
                :name="field.name"
                :label="$t('DropdownChoose', 'Vælg')"
                :invalid="!isFieldValid(field)"
                class="-primary mb-5"
                :options="field.preValues"
                @change.native="checkConditions()"
              >
                <template v-slot:options>
                  <option
                    v-for="option in field.preValues"
                    :key="option.key"
                    class="a-select__option"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </template>
              </base-select>
            </template>

            <template v-if="isCheckbox(field) || isDataConsent(field)">
              <div>
                <base-checkbox
                  :id="field.id"
                  v-model="field.defaultValue"
                  v-validate="createValidationString(field)"
                  :field-id="`field_${field.id}`"
                  :required="field.required ? 'required' : ''"
                  :value="'true'"
                  :name="field.name"
                  :label="field.label"
                  :invalid="!isFieldValid(field)"
                  :disabled="false"
                  class="mb-0 w-100"
                  @click.native="checkConditions()"
                />
                <p
                  v-if="isDataConsent(field) && field.extraData"
                  class="m-fieldset__extra-text"
                  v-html="field.extraData.acceptCopy"
                />
              </div>
            </template>
            <template v-if="isMultipleChoice(field)">
              <base-checkbox
                v-for="(prevalue, i) in field.preValues"
                :id="field.id + i"
                :key="prevalue.index"
                v-model="multipleChoice[field.id+i]"
                v-validate="field.regEx"
                :name="field.name"
                :value="prevalue.value"
                :label="prevalue.label"
                class="mb-0 w-100"
                @click.native="checkConditions()"
              />
            </template>
            <template v-if="isRadio(field)">
              <div v-if="isRatingStars(field)" class="d-flex">
                <base-rating-stars
                  v-for="(prevalue, i) in field.preValues"
                  :id="field.id"
                  :key="prevalue.index"
                  v-model="field.value"
                  v-validate="field.regEx"
                  :index="i"
                  :name="field.name"
                  :value="prevalue.value"
                  :prevalues="field.preValues"
                  @click.native="checkConditions()"
                />
              </div>
              <div v-else>
                <base-radio
                  v-for="(prevalue, i) in field.preValues"
                  :id="field.id"
                  :key="prevalue.index"
                  v-model="field.value"
                  v-validate="field.regEx"
                  :name="field.name + i"
                  :value="prevalue.value"
                  :label="prevalue.label"
                  class="mb-0 w-100"
                  @click.native="checkConditions()"
                />
              </div>
            </template>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
<script>
import formResolverMixin from '@/mixins/formResolverMixin';

export default {
  name: 'BaseFieldset',
  mixins: [formResolverMixin],
  props: {
    fieldset: {
      type: Object,
      default () {
        return {}
      }
    },
    form: {
      type: Object,
      default () {
        return {}
      }
    }
  },
  data () {
    return {
      multipleChoice: []
    }
  },
  mounted () {
   this.$nextTick(()=>{
       this.checkConditions()
    });
  },
  inject: ['$validator'],
  methods: {
    getLabel (field) {
      return `${field.label} ${field.required ? '*' : ''}`;
    },
    isFieldValid (field) {
      return !this.errors?.items?.some(err => {
        return err.field === field.name
      })
    },
    getFieldTemplateType (fieldItem) {
      let item = this.fieldTypes.filter(item => item.fieldType.includes(fieldItem.toLowerCase()));
      return this.$firstOrDefault(item)?.componentType;
    },
    createValidationString (field) {
      let string = '';
      if(field?.required) {
        string += 'required'
      }
      if(field?.required && this.$isNotNullOrUndefined(field?.regEx)) {
        string += '|'
      }
      if(this.$isNotNullOrUndefined(field?.regEx)) {
        string += 'regex:'+field.regEx
      }
      return string
    },
    checkConditions () {
      setTimeout(function () {
        if(this.$isNotNullOrUndefined(this.form)){
          this.form?.pages?.forEach((page)=>{
          page?.fieldSets.forEach((fieldset)=>{
            if (this.$isNotNullOrUndefined(fieldset) && fieldset.containers.length > 0) {
              fieldset.containers.forEach(container => {
                  container.fields.forEach(field => {
                    this.condition(field)
                  });
                });
              }
            });
          });
        }
       }.bind(this), 100)
    },
    condition (field) {
      let condition = field?.condition;
      let visible =
        !this.$isNotNullOrUndefined(condition) || condition.actionType.toLowerCase() == 'hide' ||
        !this.$isNotNullOrUndefined(condition.actionType)
          ? true
          : false;
      let logicType = condition?.logicType.toLowerCase();
      let matchCount = 0;

      condition?.rules.forEach(rule => {
        let ruleValue = this.$firstOrDefault(rule.values);
        let inputValue = document.getElementById('field_' + rule.fieldId)?.value;

        switch (rule.operator.toLowerCase()) {
          case 'is':
          case 'set_any':
            if (inputValue == ruleValue) matchCount++;
            break;

          case 'isnot':
            if (inputValue != ruleValue) matchCount++;
            break;

          case 'greaterthen':
            if (inputValue > ruleValue) matchCount++;
            break;

          case 'lessthen':
            if (inputValue < ruleValue) matchCount++;
            break;

          case 'contains':
            if (inputValue.includes(ruleValue)) matchCount++;
            break;

          case 'startswith':
            if (inputValue.startsWith(ruleValue)) matchCount++;
            break;

          case 'endswith':
            if (inputValue.endsWith(ruleValue)) matchCount++;
            break;

        }
      });

      if ( (logicType == 'any' || logicType == 'set_any') && matchCount > 0) visible = !visible;
      if ( logicType == 'all' && matchCount == condition?.rules?.length) visible = !visible;

      if (visible) {
        if(this.$parent.visibleFields.filter(x => x == field).length < 1) {
          this.$parent.visibleFields.push(field)
        }
      } else {
        this.$parent.visibleFields = this.$parent.visibleFields.filter(x => x != field)
      }

    },
    isBaseInput (field) {
      return this.getFieldTemplateType(field.fieldType) == 'BaseInput';
    },
    isPassword (field) {
      return this.getFieldTemplateType(field.fieldType) == 'BasePassword';
    },
    isTextarea (field) {
      return this.getFieldTemplateType(field.fieldType) == 'BaseTextarea';
    },
    isSelect (field) {
      return this.getFieldTemplateType(field.fieldType) == 'BaseSelect';
    },
    isCheckbox (field) {
      return this.getFieldTemplateType(field.fieldType) == 'BaseCheckbox';
    },
    isRatingStars (field) {
      return field.name === 'ratingStars';
    },
    isRadio (field) {
      return this.getFieldTemplateType(field.fieldType) == 'BaseRadio';
    },
    isDataConsent (field) {
      return this.getFieldTemplateType(field.fieldType) == 'DataConsent';
    },
    isHidden (field) {
      return this.getFieldTemplateType(field.fieldType) == 'Hidden';
    },
    isDate (field) {
      return this.getFieldTemplateType(field.fieldType) == 'Date';
    },
    isMultipleChoice (field) {
      return this.getFieldTemplateType(field.fieldType) == 'MultipleChoice';
    },
    isTitleAndDescription (field) {
      return this.getFieldTemplateType(field.fieldType) == 'TitleAndDescription';
    },
    isVisible (field) {
      return this.$isNotNullOrUndefined(this.$parent.visibleFields.filter(item => item == field)) && this.$parent.visibleFields.filter(item => item == field).length > 0;
    }
  }
};
</script>